import { useRef } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactCard from '../components/ContactCard';

const seoWins = [
  {
    heading: '51% lift in organic leads in 90 days',
    detail: 'Technical fixes + content hub for a B2B SaaS brand unlocked page-one rankings for 12 high-intent keywords.',
  },
  {
    heading: 'Local dominance across 5 cities',
    detail: 'Google Business Profile optimisation and review-generation increased map pack visibility for a home services client.',
  },
  {
    heading: '3x blog-to-demo conversions',
    detail: 'Strategic internal linking and intent-focused CTAs turned blog traffic into pipeline for a fintech company.',
  },
];

const seoPillars = [
  {
    title: 'Technical foundations',
    copy: 'Site health audits, Core Web Vitals improvements, schema, and crawl-budget optimisation keep search engines happy.',
  },
  {
    title: 'Content that earns links',
    copy: 'Editorial calendars, programmatic outlines, and digital PR campaigns build topical authority and earn white-hat links.',
  },
  {
    title: 'Conversion-ready experiences',
    copy: 'We pair rankings with CRO and lead magnet strategy so traffic translates into demos, quotes, and booked calls.',
  },
];

const seoSteps = [
  {
    step: 'Week 1: Deep-dive & diagnostics',
    text: 'Benchmark current performance, audit tech stack, and blueprint the content + link strategy that will move rankings quickest.',
  },
  {
    step: 'Weeks 2-4: Foundation launch',
    text: 'Ship quick technical wins, optimise priority pages, and roll out the first content cluster + backlink campaigns.',
  },
  {
    step: 'Ongoing: Scale what sticks',
    text: 'Run monthly experiments, expand keyword coverage, and report on revenue-driven metrics, not vanity traffic.',
  },
];

export default function SEOLanding() {
  const contactRef = useRef(null);
  const handleTalkClick = () => {
    contactRef.current?.focusForm?.();
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-white via-sky-50 to-indigo-100 font-sans text-slate-900">
      <Navbar onTalkClick={handleTalkClick} />

      <main className="flex-1 pt-32">
        <section id="services" className="mx-auto max-w-5xl px-6 text-center md:text-left">
          <div className="grid items-center gap-10 md:grid-cols-2">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-slate-500">SEO campaigns engineered for ROI</p>
              <h1 className="mt-4 text-4xl font-semibold md:text-5xl">Own page one and convert the clicks that matter</h1>
              <p className="mt-4 text-lg text-slate-600">
                We blend deep technical expertise with conversion-focused content to drive sustainable, qualified organic growth.
                Less fluff, more pipeline.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={handleTalkClick}
                  className="inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white shadow hover:opacity-90"
                >
                  Book an SEO strategy call →
                </button>
                <a
                  href="tel:14374944736"
                  className="inline-flex items-center justify-center rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-800 transition hover:bg-white"
                >
                  Talk now: 1‑437‑494‑4736
                </a>
              </div>
            </div>
            <div className="grid gap-4">
              {seoWins.map((win) => (
                <div key={win.heading} className="rounded-3xl border border-slate-200 bg-white/70 p-5 shadow-sm backdrop-blur">
                  <h3 className="text-lg font-semibold text-slate-900">{win.heading}</h3>
                  <p className="mt-2 text-sm text-slate-600">{win.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-6xl px-6">
          <div className="rounded-4xl grid gap-6 border border-blue-100 bg-white/80 p-8 shadow-sm backdrop-blur md:grid-cols-3">
            {seoPillars.map((pillar) => (
              <article key={pillar.title}>
                <h3 className="text-xl font-semibold text-slate-900">{pillar.title}</h3>
                <p className="mt-3 text-sm text-slate-600">{pillar.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="work" className="mx-auto mt-16 max-w-5xl px-6">
          <div className="rounded-3xl border border-slate-200 bg-white/80 p-10 shadow-sm backdrop-blur">
            <p className="text-sm uppercase tracking-[0.2em] text-slate-500">Case study</p>
            <h2 className="mt-2 text-3xl font-semibold text-slate-900">How we 5X’d demos for a regional pro-services firm</h2>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <ul className="space-y-3 text-sm text-slate-600">
                <li>• Migrated 200+ pages into an SEO-friendly architecture with schema and internal linking.</li>
                <li>• Built authority with thought-leadership content and high-authority backlinks.</li>
                <li>• Layered on conversion optimisations and lead magnets to capture demand.</li>
              </ul>
              <div className="rounded-2xl bg-slate-900/90 p-6 text-white shadow-lg">
                <p className="text-sm uppercase tracking-[0.2em] text-slate-300">Results in 6 months</p>
                <p className="mt-4 text-3xl font-semibold">+312%</p>
                <p className="text-sm text-slate-200">Organic demo requests</p>
                <p className="mt-4 text-sm text-slate-200">22 priority keywords moved to top 3 positions</p>
              </div>
            </div>
          </div>
        </section>

        <section id="process" className="mx-auto mt-16 max-w-5xl px-6">
          <div className="rounded-3xl border border-slate-200 bg-white/80 p-10 shadow-sm backdrop-blur">
            <p className="text-sm uppercase tracking-[0.2em] text-slate-500">How it works</p>
            <h2 className="mt-2 text-3xl font-semibold text-slate-900">A proven, sprint-based SEO framework</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {seoSteps.map((item) => (
                <div key={item.step} className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-500">{item.step}</h3>
                  <p className="mt-3 text-sm text-slate-600">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-4xl px-6 text-center">
          <div className="rounded-3xl border border-blue-100 bg-white/80 p-10 shadow-sm backdrop-blur">
            <h2 className="text-3xl font-semibold text-slate-900 md:text-4xl">Ready to outrank the competition?</h2>
            <p className="mt-3 text-lg text-slate-600">
              Let’s audit your current SEO performance, pinpoint the fastest wins, and map the backlog we’ll tackle together.
            </p>
            <button
              type="button"
              onClick={handleTalkClick}
              className="mt-6 inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-white shadow hover:opacity-90"
            >
              Schedule my SEO roadmap session →
            </button>
          </div>
        </section>
      </main>

      <ContactCard ref={contactRef} className="mt-16" />
      <Footer />
    </div>
  );
}
